/**
 * Task routing: a "default config" applies to every task, and an
 * optional expandable section lets the user pin a different config per
 * task type. A blank per-task value means "inherit the default".
 */
import { useState } from 'react'
import { TASK_TYPES, TASK_LABELS, type TaskType } from '@/data/llmCatalog'
import type { LlmConfig } from '@/types'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface Props {
  configs: LlmConfig[]
  defaultName: string
  overrides: Partial<Record<TaskType, string>>
  onDefaultChange: (name: string) => void
  onOverrideChange: (task: TaskType, name: string) => void
}

export default function TaskRoutingPanel({
  configs,
  defaultName,
  overrides,
  onDefaultChange,
  onOverrideChange,
}: Props) {
  const [expanded, setExpanded] = useState(false)

  if (configs.length === 0) {
    return (
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        Add at least one LLM configuration to choose how tasks are routed.
      </div>
    )
  }

  const overrideCount = Object.values(overrides).filter((v) => !!v).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Default configuration
        </label>
        <select
          className="input"
          value={defaultName}
          onChange={(e) => onDefaultChange(e.target.value)}
          style={{ maxWidth: 360 }}
        >
          {!defaultName && <option value="">Select a default…</option>}
          {configs.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Applied to every task unless overridden below.
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          border: 'none',
          color: 'var(--color-text-primary)',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          padding: '6px 0',
          width: 'fit-content',
        }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Override per task
        {overrideCount > 0 && (
          <span
            className="badge badge-info"
            style={{ fontSize: 13, marginLeft: 4 }}
          >
            {overrideCount} active
          </span>
        )}
      </button>

      {expanded && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            padding: 12,
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            background: 'var(--color-bg-elevated)',
          }}
        >
          {TASK_TYPES.map((task) => (
            <div key={task} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 180, fontSize: 14 }}>{TASK_LABELS[task as TaskType]}</div>
              <select
                className="input"
                value={overrides[task as TaskType] || ''}
                onChange={(e) => onOverrideChange(task as TaskType, e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Inherit default ({defaultName || '—'})</option>
                {configs.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
